import { useEffect, useRef, useState } from 'react';
import {
  SNOOZE_PRESETS,
  SNOOZE_PRESET_LABEL_PT,
  zonedPartsToUtc,
  type SnoozePreset,
} from '@cleartask/shared';
import { useTimeZone } from '../../session.js';

export type SnoozePayload = { preset: SnoozePreset } | { dueAt: number };

interface SnoozeMenuProps {
  label: string;
  onSelect: (payload: SnoozePayload) => void;
  /** Rotulo acessivel quando o menu age sobre varias tarefas de uma vez. */
  describedBy?: string;
}

/**
 * Menu de adiamento (spec 4.3).
 *
 * Os presets sao resolvidos por `resolveSnoozePreset` — aqui so se escolhe
 * QUAL. A data personalizada e o unico caso que produz um instante nesta
 * camada, e ela e convertida a partir do fuso do USUARIO: o `datetime-local`
 * devolve hora de parede sem fuso, e interpreta-la no fuso do dispositivo
 * gravaria o horario errado para quem estiver viajando.
 */
export function SnoozeMenu({ label, onSelect, describedBy }: SnoozeMenuProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const timeZone = useTimeZone();
  const containerRef = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora. Sem isso o menu fica aberto atras do proximo toque,
  // que no mobile e o modo mais comum de "sair" de um popover.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: globalThis.PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function choose(payload: SnoozePayload) {
    onSelect(payload);
    setOpen(false);
    setCustom('');
  }

  function submitCustom() {
    // '2026-07-31T09:00' -> partes de calendario, sem passar por `new Date`,
    // que interpretaria a string no fuso do dispositivo.
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(custom);
    if (!match) return;

    const [, year, month, day, hour, minute] = match;
    choose({
      dueAt: zonedPartsToUtc(timeZone, {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
      }),
    });
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-describedby={describedBy}
        className="min-h-touch rounded-sm px-2 text-sm text-text-mut transition-colors duration-150 hover:bg-surface-2 hover:text-text"
        onClick={() => setOpen((value) => !value)}
      >
        {label} ▾
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Adiar para"
          className="absolute right-0 z-20 mt-1 w-56 rounded border border-border bg-surface p-1 shadow-card"
        >
          {SNOOZE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              role="menuitem"
              className="flex min-h-touch w-full items-center rounded-sm px-3 text-left text-sm hover:bg-surface-2"
              onClick={() => choose({ preset })}
            >
              {SNOOZE_PRESET_LABEL_PT[preset]}
            </button>
          ))}

          <div className="mt-1 border-t border-border pt-1">
            <label className="block px-3 pb-1 text-xs text-text-mut" htmlFor="snooze-custom">
              Data personalizada
            </label>
            <div className="flex items-center gap-1 px-1 pb-1">
              <input
                id="snooze-custom"
                type="datetime-local"
                value={custom}
                onChange={(event) => setCustom(event.target.value)}
                className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
              />
              <button
                type="button"
                disabled={custom === ''}
                onClick={submitCustom}
                className="min-h-touch shrink-0 rounded-sm bg-brand-blue px-3 text-sm font-medium text-white disabled:opacity-40"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
